import React from 'react';
import { View, TouchableOpacity, Image } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import { faCalendarCheck, faLocationDot, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../services/window-information';
import { InfoInRow } from '../InfoInRow';
import { isMappedSigrcPhoneOccurrence } from '../../utils/getSigrcOccurrenceType';
import Card from '../Card';
import { RootNavigation } from '../../Routes';

export function SigrcOccurrence({ occurrence, navigation }: { occurrence: Occurrence; navigation: RootNavigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    if (occurrence.triggerType !== 'sigrc') {
        return <></>;
    }

    const resume = occurrence.resume as MappedSigrcPhoneOccurrence | SigRcResumeData;

    function addressFormat(address: { logradouro: string; numero: string; bairro: string; cidade: string; uf: string; cep: string; pontoReferencia?: string | null; }) {
        const formattedCEP = address.cep.replace(/(\d{5})(\d{3})/, '$1-$2');
        let formattedAddress = `${address.logradouro}, ${address.numero} - ${address.bairro}, ${address.cidade} - ${address.uf}, ${formattedCEP}`;
        if (address.pontoReferencia) {
            formattedAddress += ` (${formattedAddress})`;
        }
        return formattedAddress;
    }

    return (
        <View style={{ gap: 20 }}>
            <View style={{ gap: 5 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <TouchableOpacity
                            onPress={() => {
                                window.open(`/dispatch/occurrences/detail/${occurrence?.id}`);
                            }}
                            disabled={windowInfo.isMobile}
                        >
                            <MyAppText style={{ fontSize: 16, color: theme.colors.textColor, fontWeight: 'bold' }}>
                                {translate('nProtocol')}: {occurrence?.id}
                            </MyAppText>
                        </TouchableOpacity>
                        <MyAppText> </MyAppText>
                        <MyAppText style={{ fontSize: 14, color: theme.colors.textColor }}>
                            ({translate('team')} {translate(occurrence.OccurrenceUnits?.length && occurrence.OccurrenceUnits[0].OccurrenceUnit.isStarter ? 'starter' : 'support').toLowerCase()})
                        </MyAppText>
                    </View>
                    <TouchableOpacity onPress={() => navigation.navigate('AppAgent', { screen: 'AgentOccurrences' })}>
                        <FontAwesomeIcon color={theme.colors.iconColor} fontSize={30} icon={faTimes} />
                    </TouchableOpacity>
                </View>
                <MyAppText style={styles.alertText}>
                    {translate('sigrcAlert')}
                </MyAppText>
                {(occurrence.resume as SigRcResumeData).imageUrl ?
                    <View style={styles.imageBox}>
                        <Image source={{ uri: (occurrence.resume as SigRcResumeData).imageUrl }} style={styles.image} />
                    </View> : <></>
                }
            </View>
            <Card>
                <View style={{ gap: 15 }}>
                    <View style={{ gap: 5 }}>
                        {isMappedSigrcPhoneOccurrence(resume) ? (
                            <View style={{ rowGap: 5 }}>
                                <InfoInRow value={resume.sigrcId} title='sigrcId' />
                                <InfoInRow value={resume.externalProtocol} title='externalProtocol' />
                                <InfoInRow value={resume.content.tipoSolicitacao} title='type' />
                                <InfoInRow value={resume.content.dataCadastro} title='registeredAt' isDate={true} />
                                <InfoInRow value={resume.content.criticidade} title='criticality' />
                            </View>
                        ) : (
                            <View style={{ rowGap: 5 }}>
                                <InfoInRow value={resume.content.id} title='sigrcId' />
                                <InfoInRow value={resume.content.protocoloExterno} title='externalProtocol' />
                                <InfoInRow value={resume.content.tipoSolicitacao.descricao} title='type' />
                                {resume.content.tipoSolicitacao.sla ?
                                    <>
                                        <InfoInRow value={resume.content.tipoSolicitacao.sla?.status} title='status' />
                                        <InfoInRow value={resume.content.tipoSolicitacao.sla?.nome} title='completionTime' />
                                    </>
                                    : <></>
                                }
                                <InfoInRow value={resume.content.tipoSolicitacao.dataCadastro} title='registeredAt' isDate={true} />
                                <InfoInRow value={resume.content.tipoSolicitacao.criticidade} title='criticality' />
                            </View>
                        )}
                    </View>
                    <View style={{ gap: 3 }}>
                        <MyAppText style={styles.text}>{translate('location')}</MyAppText>
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <MyAppText style={styles.detectionInfoText}>
                                {moment(new Date(occurrence.createdAt)).format('DD/MM/YYYY HH:mm:ss')}
                            </MyAppText>
                        </View>
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faLocationDot} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <MyAppText style={styles.detectionInfoText}>
                                {addressFormat(occurrence.resume.content.endereco)}
                            </MyAppText>
                        </View>
                    </View>
                </View>
                <View>
                    <View style={styles.visualizedRow}>
                        <MyAppText style={styles.cardBoldText}>{translate('occurrenceId')}:</MyAppText>
                        <MyAppText style={styles.cardText}>{occurrence.id}</MyAppText>
                    </View>
                    <View style={styles.visualizedRow}>
                        <MyAppText style={styles.cardBoldText}>{translate('priority')}:</MyAppText>
                        <MyAppText style={[styles.cardText, occurrence.priority === 'maximum' ? { color: '#FF0027' } : undefined]}>{occurrence.priority ? translate(occurrence.priority) : ''}</MyAppText>
                    </View>
                    <View>
                        <View>
                            <MyAppText style={styles.cardBoldText}>{translate('narrative')}:</MyAppText>
                            <MyAppText style={styles.cardText}>{occurrence.narrative}</MyAppText>
                        </View>
                    </View>
                </View>
                {isMappedSigrcPhoneOccurrence(resume) && (
                    <>
                        <View>
                            <View style={styles.visualizedRow}>
                                <MyAppText style={styles.cardBoldText}>
                                    {translate('systemData')}
                                </MyAppText>
                            </View>

                            <InfoInRow value={resume.occurrenceSize} title='occurrenceSize' />
                            <InfoInRow value={resume.occurrenceDate} title='occurrenceDate' />
                            <InfoInRow value={resume.victimsCount} title='victimsCount' />
                            <InfoInRow value={resume.vehiclesCount} title='vehiclesCount' />
                            <InfoInRow value={resume.propertyType} title='propertyType' />
                            <InfoInRow value={resume.residencesCount} title='residencesCount' />
                            <InfoInRow value={resume.buildingsCount} title='buildingsCount' />
                            <InfoInRow value={resume.victimsDeficientCount} title='victimsDeficientCount' />
                            <InfoInRow value={resume.isPoliceVehicleInPlace} title='isPoliceVehicleInPlace' />
                            <InfoInRow value={resume.homelessCount} title='homelessCount' />
                            <InfoInRow value={resume.blockedPassage} title='blockedPassage' />
                            <InfoInRow value={resume.specificLocation} title='specificLocation' />
                            <InfoInRow value={resume.vehicleInjured} title='vehicleInjured' />
                            <InfoInRow value={resume.propertyInjured} title='propertyInjured' />
                            <InfoInRow value={resume.victimsDescription} title='victimsDescription' />
                        </View>
                        {Array.isArray(resume.victims) && resume.victims.length > 0 && (
                            <View style={{ rowGap: 1 }}>
                                <MyAppText style={styles.cardBoldText}>
                                    {translate('registeredVictims')}
                                </MyAppText>

                                {resume.victims && resume.victims.map((victim, index) => (
                                    <View style={{ rowGap: 1, display: 'flex', flexDirection: 'column' }} key={victim.id}>
                                        <InfoInRow value={victim.estado} title='victimState' titleSuffix={String(index + 1)} />
                                        <InfoInRow value={victim.sexo} title='victimSex' titleSuffix={String(index + 1)} />
                                    </View>
                                ))}
                            </View>
                        )}
                    </>
                )}
            </Card>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    visualizedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
    detectionInfoText: {
        color: theme.colors.textColor,
        fontSize: 12
    },
    text: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 16
    },
    alertText: {
        color: theme.colors.textColor,
        fontSize: 16,
        fontWeight: 'bold',
    },
    cardText: {
        color: theme.colors.textColor,
        fontSize: 12
    },
    cardBoldText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 14
    },
    imageBox: {
        height: 314,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    image: {
        height: 314,
        width: 311,
        objectFit: 'contain',
        borderRadius: 8,
    },
}));
